import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Table,
  Modal,
  Button,
  Combobox,
  DateInput,
  NumberInput,
  Badge,
} from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { AUDIT_COLUMNS, sortByLabel } from '../../../config/columns';
import {
  buySellService,
  bankAccountService,
  currencyService,
  customerService,
  settingService,
} from '../../../services/resources';
import PrintSlip from './PrintSlip';

const TYPES = [
  { value: 'BUY', label: 'BUY (Beli)' },
  { value: 'SELL', label: 'SELL (Jual)' },
];

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + '01';

const emptyForm = () => ({
  transaction_type: 'BUY',
  transaction_date: today(),
  bank_id: '',
  currency_id: '',
  valas: '',
  kurs: '',
  customer_id: '',
  rate_difference: '',
  remark: '',
  idempotency_key: '',
});

function computeAmount(type, valas, kurs, rateDiff) {
  const terima = (Number(valas) || 0) * (Number(kurs) || 0);
  const rd = Number(rateDiff) || 0;
  return type === 'SELL' ? terima + rd : terima - rd;
}

export default function BuySellPage({ onToast }) {
  const { can } = useAuth();
  const canView = can('BUY_SELL', 'CanView');
  const canCreate = can('BUY_SELL', 'CanCreate');
  const canEdit = can('BUY_SELL', 'CanEdit');
  const canDelete = can('BUY_SELL', 'CanDelete');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Date range filter (server-side). Initial display mirrors the server default.
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());

  const [banks, setBanks] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [printRow, setPrintRow] = useState(null);
  const [store, setStore] = useState(null);

  const load = async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try {
      setRows(await buySellService.list(from, to));
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Date filter change -> server round-trip with the new range.
  const changeDateFrom = (v) => {
    setDateFrom(v);
    load(v, dateTo);
  };
  const changeDateTo = (v) => {
    setDateTo(v);
    load(dateFrom, v);
  };

  useEffect(() => {
    load();
    Promise.all([bankAccountService.list(), currencyService.list(), customerService.list()])
      .then(([b, c, cu]) => {
        setBanks(b);
        setCurrencies(c);
        setCustomers(cu);
      })
      .catch(() => {});
    settingService.get().then(setStore).catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Print a row: render its slip into the hidden .print-area, then trigger the
  // browser print dialog once the DOM has the new content.
  const printTransaction = (row) => setPrintRow(row);
  useEffect(() => {
    if (!printRow) return;
    const timer = setTimeout(() => window.print(), 50);
    const reset = () => setPrintRow(null);
    window.addEventListener('afterprint', reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('afterprint', reset);
    };
  }, [printRow]);

  const bankOptions = sortByLabel(banks
    .filter((b) => !b.is_blocked)
    .map((b) => ({ value: b.id, label: b.bank_name })));
  // IDR can never be bought/sold against itself — hide it from this form only.
  const currencyOptions = sortByLabel(currencies
    .filter((c) => c.currency_code !== 'IDR')
    .map((c) => ({ value: c.id, label: c.currency_code })));
  // Blocked customers can't be selected for a new transaction.
  const customerOptions = sortByLabel(customers
    .filter((c) => !c.is_blocked)
    .map((c) => ({ value: c.id, label: c.customer_name })));

  // Per-field validity for live error clearing (Phase 2b item 2).
  const FIELD_VALID = {
    transaction_date: (v) => !!v,
    bank_id: (v) => !!v,
    currency_id: (v) => !!v,
    customer_id: (v) => !!v,
    valas: (v) => Number(v) > 0,
    kurs: (v) => Number(v) > 0,
  };

  const set = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => {
      if (!prev[k]) return prev;
      const check = FIELD_VALID[k];
      if (check && check(v)) {
        const next = { ...prev };
        delete next[k];
        return next;
      }
      return prev;
    });
  };

  const selectedCurrency = currencies.find((c) => c.id === Number(form.currency_id));
  const amount = computeAmount(form.transaction_type, form.valas, form.kurs, form.rate_difference);

  // Blocking kurs range check — the backend enforces this too; this is an
  // early client-side echo of the same rule so Save doesn't round-trip first.
  const kursWarning = useMemo(() => {
    if (!selectedCurrency || !form.kurs) return '';
    const k = Number(form.kurs);
    const { min_rate, max_rate } = selectedCurrency;
    if ((min_rate != null && k < Number(min_rate)) || (max_rate != null && k > Number(max_rate))) {
      return 'Kurs is outside the normal range for this currency.';
    }
    return '';
  }, [selectedCurrency, form.kurs]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const keys = ['doc_no', 'transaction_type', 'bank_name', 'currency_code', 'customer_name', 'remark'];
    return rows.filter((r) => keys.some((k) => String(r[k] ?? '').toLowerCase().includes(term)));
  }, [rows, search]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), idempotency_key: crypto.randomUUID() });
    setErrors({});
    setSubmitError('');
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      transaction_type: row.transaction_type,
      transaction_date: String(row.transaction_date).slice(0, 10),
      bank_id: row.bank_id,
      currency_id: row.currency_id,
      valas: row.valas,
      kurs: row.kurs,
      customer_id: row.customer_id,
      rate_difference: row.rate_difference,
      remark: row.remark || '',
      idempotency_key: row.idempotency_key,
    });
    setErrors({});
    setSubmitError('');
    setOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!form.transaction_date) e.transaction_date = 'Date is required';
    if (!form.bank_id) e.bank_id = 'Bank is required';
    if (!form.currency_id) e.currency_id = 'Currency is required';
    if (!form.customer_id) e.customer_id = 'Customer is required';
    if (!(Number(form.valas) > 0)) e.valas = 'Must be greater than 0';
    if (!(Number(form.kurs) > 0)) e.kurs = 'Must be greater than 0';
    else if (kursWarning) e.kurs = kursWarning;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (savingRef.current) return; // guard against double-submit
    if (!validate()) return;
    savingRef.current = true;
    setSaving(true);
    setSubmitError('');
    const payload = {
      transaction_type: form.transaction_type,
      transaction_date: form.transaction_date,
      bank_id: Number(form.bank_id),
      currency_id: Number(form.currency_id),
      valas: Number(form.valas),
      kurs: Number(form.kurs),
      customer_id: Number(form.customer_id),
      rate_difference: Number(form.rate_difference) || 0,
      remark: form.remark || null,
      idempotency_key: form.idempotency_key,
    };
    try {
      if (editing) {
        await buySellService.update(editing.id, payload);
        onToast?.('Transaction updated');
      } else {
        await buySellService.create(payload);
        onToast?.('Transaction saved');
      }
      setOpen(false);
      load();
    } catch (err) {
      setSubmitError(apiError(err).message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await buySellService.remove(deleteTarget.id);
      onToast?.('Transaction deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    }
  };

  if (!canView) {
    return (
      <Card title="Buy / Sell">
        <p className="py-6 text-center text-sm text-gray-500">
          You don’t have permission to view this page.
        </p>
      </Card>
    );
  }

  const columns = [
    { key: 'doc_no', label: 'Doc No' },
    { key: 'transaction_date', label: 'Date', render: (r) => String(r.transaction_date).slice(0, 10) },
    {
      key: 'transaction_type',
      label: 'Type',
      render: (r) => (
        <Badge tone={r.transaction_type === 'BUY' ? 'blue' : 'green'}>{r.transaction_type}</Badge>
      ),
    },
    { key: 'bank_name', label: 'Bank' },
    { key: 'currency_code', label: 'Currency' },
    { key: 'valas', label: 'Valas', render: (r) => fmt(r.valas), className: 'text-right' },
    { key: 'kurs', label: 'Kurs', render: (r) => fmt(r.kurs), className: 'text-right' },
    { key: 'customer_name', label: 'Customer' },
    { key: 'amount', label: 'Amount', render: (r) => fmt(r.amount), className: 'text-right' },
    { key: 'remark', label: 'Remark' },
    ...AUDIT_COLUMNS,
    {
      key: '__a',
      label: 'Actions',
      className: 'text-right whitespace-nowrap',
      render: (r) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="secondary" onClick={() => printTransaction(r)}>
            Print
          </Button>
          {canEdit && (
            <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="danger" onClick={() => setDeleteTarget(r)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card title="Buy / Sell" actions={canCreate ? <Button onClick={openCreate}>+ New</Button> : null}>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <DateInput label="Date From" value={dateFrom} onChange={changeDateFrom} />
        </div>
        <div className="w-40">
          <DateInput label="Date To" value={dateTo} onChange={changeDateTo} />
        </div>
        <div className="flex flex-1 items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
          {search && (
            <Button type="button" variant="ghost" onClick={() => setSearch('')}>
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable, no pagination — the date range bounds the row count. */}
      <Table
        columns={columns}
        rows={filtered}
        loading={loading}
        maxHeight="60vh"
        className="rounded-md border border-gray-100"
      />
      <p className="mt-2 text-xs text-gray-400">{filtered.length} transaction(s)</p>

      {/* Create / Edit modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`${editing ? 'Edit' : 'New'} Buy / Sell${editing ? ` — ${editing.doc_no}` : ''}`}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-3">
          {submitError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {/* Row 1: Type | Date */}
            <div>
              <span className="mb-1 block text-sm font-medium text-gray-700">
                Type<span className="text-red-500"> *</span>
              </span>
              <div className="inline-flex overflow-hidden rounded-md border border-brand">
                {TYPES.map((t) => {
                  const active = form.transaction_type === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      disabled={!!editing}
                      onClick={() => set('transaction_type', t.value)}
                      className={`px-4 py-2 text-sm font-medium transition-colors first:border-r first:border-brand disabled:cursor-not-allowed ${
                        active ? 'bg-brand text-white' : 'bg-white text-brand hover:bg-brand-light'
                      } ${!!editing && !active ? 'opacity-50' : ''}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <DateInput
              label="Date"
              required
              value={form.transaction_date}
              error={errors.transaction_date}
              onChange={(v) => set('transaction_date', v)}
            />

            {/* Row 2: Customer | Currency */}
            <Combobox
              label="Customer"
              required
              placeholder="-- select customer --"
              options={customerOptions}
              value={form.customer_id}
              error={errors.customer_id}
              onChange={(v) => set('customer_id', v)}
            />
            <Combobox
              label="Currency"
              required
              placeholder="-- select currency --"
              options={currencyOptions}
              value={form.currency_id}
              error={errors.currency_id}
              onChange={(v) => set('currency_id', v)}
            />

            {/* Row 3: Valas | Kurs */}
            <NumberInput
              label="Valas"
              required
              value={form.valas}
              error={errors.valas}
              onChange={(v) => set('valas', v)}
            />
            <div>
              <NumberInput
                label="Kurs"
                required
                value={form.kurs}
                error={errors.kurs}
                onChange={(v) => set('kurs', v)}
              />
              {kursWarning && (
                <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-700">
                  ⚠ {kursWarning}
                </p>
              )}
            </div>

            {/* Row 4: Rate Difference | Bank */}
            <div>
              <NumberInput
                label="Rate Difference"
                allowNegative
                value={form.rate_difference}
                onChange={(v) => set('rate_difference', v)}
              />
              <p className="mt-1 text-xs text-gray-500">
                Positive = profit, negative = loss. This affects the calculated amount accordingly.
              </p>
            </div>
            <Combobox
              label="Bank"
              required
              placeholder="-- select bank --"
              options={bankOptions}
              value={form.bank_id}
              error={errors.bank_id}
              onChange={(v) => set('bank_id', v)}
            />
          </div>

          {/* Row 5: Remark (full width) */}
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Remark</span>
            <textarea
              value={form.remark}
              onChange={(e) => set('remark', e.target.value)}
              style={{ resize: 'none' }}
              className="h-16 w-full overflow-y-auto rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </label>

          {/* Row 6: Amount (calculated), full width, last */}
          <div className="rounded-md bg-brand-light px-3 py-2">
            <span className="text-sm text-gray-600">Amount (calculated)</span>
            <div className="text-lg font-semibold text-brand-dark">{fmt(amount)}</div>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Transaction"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">This will be permanently deleted. Continue?</p>
      </Modal>

      <PrintSlip
        row={printRow}
        customer={printRow ? customers.find((c) => c.id === printRow.customer_id) : null}
        currency={printRow ? currencies.find((c) => c.id === printRow.currency_id) : null}
        store={store}
      />
    </Card>
  );
}
